// Hook: server-round-trip for the architecture tree.
//
// Owns:
//   - `saving` boolean (button state)
//   - `invalidNodeIds` array (red-outline highlight after a 422)
//   - `onSave` fn — pushes the current tree, re-hydrates from GET, or extracts
//     the server's validation-error node ids and shows the toast
//
// Auto-clears `invalidNodeIds` on the next tree edit (`doc` identity change)
// so users aren't stuck staring at stale red rings after they've made fixes.

import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, extractErrorNodeIds, formatApiErrorDetail } from "../lib/api";

export function useHierarchyPatch({ projectId, arch, dispatch, dirty, doc }) {
  const [saving, setSaving] = useState(false);
  const [invalidNodeIds, setInvalidNodeIds] = useState([]);
  const [pendingRestoreLabel, setPendingRestoreLabel] = useState(null);
  const lastValidatedDocRef = useRef(null);

  // Drop the red-highlight the moment the user starts editing again.
  useEffect(() => {
    if (invalidNodeIds.length === 0) return;
    if (lastValidatedDocRef.current !== null && lastValidatedDocRef.current !== doc) {
      setInvalidNodeIds([]);
    }
    lastValidatedDocRef.current = doc;
  }, [doc, invalidNodeIds.length]);

  const onSave = useCallback(async () => {
    if (!arch || !dirty || saving) return;
    setSaving(true);
    try {
      const payload = {
        nodes: arch.nodes,
        peer_connections: arch.peerConnections,
      };
      if (pendingRestoreLabel) payload.label = pendingRestoreLabel;
      const { data } = await api.patch(`/projects/${projectId}/hierarchy`, payload);
      const { data: fresh } = await api.get(`/projects/${projectId}/hierarchy`);
      dispatch({ type: "LOAD_FROM_SERVER", doc: fresh });
      setPendingRestoreLabel(null);
      setInvalidNodeIds([]);
      toast.success("Saved", { description: `Version v${data.version.version} created.` });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const ids = extractErrorNodeIds(detail);
      setInvalidNodeIds(ids);
      // Anchor to the current doc so the next tree edit clears the highlight.
      lastValidatedDocRef.current = doc;
      toast.error(
        e.response?.status === 422 ? "Invalid hierarchy — can't save" : "Save failed",
        { description: formatApiErrorDetail(detail) || e.message },
      );
    } finally {
      setSaving(false);
    }
  }, [arch, dirty, saving, projectId, pendingRestoreLabel, dispatch, doc]);

  return {
    saving,
    invalidNodeIds,
    pendingRestoreLabel,
    setPendingRestoreLabel,
    onSave,
  };
}
